/* eslint-disable camelcase */
/* eslint-disable max-len */
import React from "react"
import { useSelector } from "react-redux"

import { SilResultSubsystem } from "server/models"
import { LangType, StoreType, UserSettings } from "client/store"

import * as Const from "client/components/pdfmake/template/const"
import FormatPercentage, { FormatPercentageString } from "client/pages/sil/format/percentage"

import {
    ResultTable,
    ResultTableAnnotation,
    BlockContent,
} from "../../styled"


interface IsaTr842002Props {
    subsystemList: readonly SilResultSubsystem[]
    showArchitectureConstraints: boolean
}


export const IsaTr842002 = ({
    subsystemList,
    showArchitectureConstraints,
}: IsaTr842002Props) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <BlockContent>
            <ResultTable>
                <thead>
                    <tr>
                        <th>{lang.subsystem}</th>
                        <th>{lang.architecture}</th>
                        {
                            showArchitectureConstraints
                            && <th>{lang.architecturalConstraints}</th>
                        }
                        <th>{lang.t1}</th>
                        <th>{lang.mttr}</th>
                        <th>{lang.beta}</th>
                        <th>{lang.s_f}</th>
                        <th>{lang.d_f}</th>
                    </tr>
                </thead>

                <tbody>
                    {
                        subsystemList.map((subsystem) => {
                            const mttr = subsystem.tr842002mttr
                            const beta = <FormatPercentage data={subsystem.tr842002beta} tableView />
                            const t1 = subsystem.tr842002t1
                            const safeFailure = subsystem.tr842002safeFailure
                            const dangerousFailure = subsystem.tr842002dangerousFailure

                            // TODO: check usage in architectures <<<<
                            return (
                                <tr key={subsystem.id}>
                                    <td>{subsystem.name}</td>
                                    <td>{subsystem.architecture}</td>
                                    {
                                        showArchitectureConstraints
                                        && <td>{lang[subsystem.architecturalConstraints]}</td>
                                    }
                                    <td>{t1}</td>
                                    <td>{mttr}</td>
                                    <td>{beta}</td>
                                    <td>{safeFailure}</td>
                                    <td>{dangerousFailure}</td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </ResultTable>

            <ResultTableAnnotation>
                {`${lang.n_a} = ${lang.notApplicable}, ${lang.s_f} = ${lang.safeSystematicFailure}, ${lang.d_f} = ${lang.dangerousSystematicFailure}`}
            </ResultTableAnnotation>
        </BlockContent>
    )
}


export const IsaTr842002PdfExport = (
    subsystemList: readonly SilResultSubsystem[],
    showArchitectureConstraints: boolean,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const stack: any[] = []

    subsystemList.forEach((subsystem) => {
        const t1 = subsystem.tr842002t1
        const mttr = subsystem.tr842002mttr
        const beta = FormatPercentageString(subsystem.tr842002beta, userSettings, lang)
        const safeFailure = subsystem.tr842002safeFailure
        const dangerousFailure = subsystem.tr842002dangerousFailure

        // TODO: check usage in architectures <<<<

        const list: string[] = [
            `${lang.architecture}: ${subsystem.architecture}`,
        ]
        if (showArchitectureConstraints) {
            list.push(`${lang.architecturalConstraints}: ${lang[subsystem.architecturalConstraints]}`)
        }
        list.push(
            `${lang.t1}: ${t1} ${lang.hours}`,
            `${lang.mttr}: ${mttr} ${lang.hours}`,
            `${lang.beta}: ${beta}`,
            `${lang.s_f}: ${safeFailure}`,
            `${lang.d_f}: ${dangerousFailure}`,
        )

        stack.push(
            {
                bold: true,
                text: `${lang.subsystem} - ${subsystem.name}`,
                margin: Const.SUBSYSTEM_NAME_MARGIN,
            },
            {
                style: Const.LIST,
                ul: list,
            },
        )
    })

    return stack
}
